import { RefObject } from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useTranslation } from 'react-i18next';
import { ErrorCaption, FieldLabel, FormField } from '../styled';
import { ACCEPTED_EXTENSIONS, formatFileSize } from '../utils';

interface DocumentUploadFieldProps {
  fileInputRef: RefObject<HTMLInputElement>;
  selectedFile: File | null;
  fileError: string;
  isSubmitting: boolean;
  uploadPct: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const DocumentUploadField = ({
  fileInputRef,
  selectedFile,
  fileError,
  isSubmitting,
  uploadPct,
  onChange,
}: DocumentUploadFieldProps) => {
  const { t } = useTranslation('common');

  return (
    <FormField>
      <FieldLabel>{t('assistant.ragStorageDialog.document')}</FieldLabel>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS}
        style={{ display: 'none' }}
        onChange={onChange}
        data-testid="context-dialog-file-input"
      />
      {selectedFile ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: '10px 12px',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'var(--am-border-mid)',
            cursor: 'pointer',
          }}
          onClick={() => fileInputRef.current?.click()}
          data-testid="context-dialog-upload-box"
        >
          <InsertDriveFileOutlinedIcon
            sx={{ color: 'var(--am-text-secondary)', fontSize: 20, flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={selectedFile.name} sx={{ color: 'var(--am-text-primary)' }}>
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--am-text-secondary)' }}>
              {formatFileSize(selectedFile.size)}
            </Typography>
          </Box>
          <Tooltip title={t('assistant.ragStorageDialog.changeFile')}>
            <AttachFileIcon
              sx={{ color: 'var(--am-text-secondary)', fontSize: 18, flexShrink: 0 }}
            />
          </Tooltip>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: '14px 12px',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: fileError ? 'error.main' : 'var(--am-border-heavy)',
            cursor: 'pointer',
            '&:hover': { borderColor: 'var(--am-focus-border-color)' },
          }}
          onClick={() => fileInputRef.current?.click()}
          data-testid="context-dialog-upload-box"
        >
          <AttachFileIcon sx={{ color: 'var(--am-text-secondary)', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: 'var(--am-text-secondary)' }}>
            {t('assistant.ragStorageDialog.clickToSelectFile')}
          </Typography>
        </Box>
      )}
      {fileError && (
        <ErrorCaption variant="caption" color="error">
          {fileError}
        </ErrorCaption>
      )}
      {isSubmitting && uploadPct > 0 && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={uploadPct}
            data-testid="context-dialog-upload-progress"
          />
          <Typography
            variant="caption"
            sx={{ color: 'var(--am-text-secondary)', mt: 0.5, display: 'block' }}
          >
            {t('assistant.ragStorageDialog.uploading', { pct: uploadPct })}
          </Typography>
        </Box>
      )}
    </FormField>
  );
};

export default DocumentUploadField;
